import { AppGlueCoreInit } from "../../../../../AppGlueCoreInit";
import { DataDefinition } from "../../../../../rule-engine-core/Common/Data/DataDefinition";
import { BooleanDataDefinition } from "../../../../../rule-engine-core/Common/Data/Definitions/BooleanDataDefinition";
import { StringDataDefinition } from "../../../../../rule-engine-core/Common/Data/Definitions/StringDataDefinition";
import { ExecutionData } from "../../../../../rule-engine-core/Common/Data/ExecutionData/ExecutionData";
import { ExpressionRule } from "../../../../../rule-engine-core/Expressions/ExpressionRule";
import {
    IfThenExpression,
    IfThenPair,
    IfThenStyle,
} from "../../../../../rule-engine-core/Expressions/Expressions/Logic/IfThenExpression";
import { LessThanEqualToExpression } from "../../../../../rule-engine-core/Expressions/Expressions/Logic/LessThanEqualToExpression";
import { NotExpression } from "../../../../../rule-engine-core/Expressions/Expressions/Logic/NotExpression";
import { StringContainsExpression } from "../../../../../rule-engine-core/Expressions/Expressions/String/StringContainsExpression";
import { StringEqualExpression } from "../../../../../rule-engine-core/Expressions/Expressions/String/StringEqualExpression";
import { StringLengthExpression } from "../../../../../rule-engine-core/Expressions/Expressions/String/StringLengthExpression";
import { StringStartWithExpression } from "../../../../../rule-engine-core/Expressions/Expressions/String/StringStartWithExpression";
import { StatementRuleConfiguration } from "../../../../../rule-engine-core/Rule/Statement/StatementRuleConfiguration";
import type { EnrichedPart } from "../../../domain/types";
import type { DemoOutputQueue, DemoRuleStatus } from "../../../domain/ruleTypes";
import {
    runStatementRuleBoolean,
} from "../../executableFamilies/statementRule/StatementRuleIntegrationAdapter";
import { createNativeStatementRuleSummaryPresenter } from "../../executableFamilies/statementRule/nativeStatementRuleSummaryPresenter";
import { requireRulePolicyBinding } from "../rulePolicyBindingRegistry";
import type { StatementRuleIntegrationPolicy } from "../statementRulePolicyTypes";

export const DQ004_RULE_ID = "DQ-004";
export const DQ004_APPGLUE_EXECUTABLE_TYPE = "StatementRuleConfiguration";
export const DQ004_OUTPUT_QUEUE: DemoOutputQueue = "quarantine_records";
export const DQ004_MIN_MPN_LENGTH = 3;
export const DQ004_PLACEHOLDER_MPN = "TBD";
export const DQ004_MANUFACTURER_PREFIX_RULES = [
    { manufacturerName: "Texas Instruments", prefix: "TI-" },
    { manufacturerName: "Murata", prefix: "GRM" },
    { manufacturerName: "Yageo", prefix: "RC" },
    { manufacturerName: "Vishay", prefix: "VJ" },
    { manufacturerName: "STMicroelectronics", prefix: "STM" },
    { manufacturerName: "Unknown / New", prefix: "NEW-" },
] as const;

export type Dq004MpnIssue = "placeholder" | "whitespace" | "too_short" | "manufacturer_prefix_mismatch";

export type Dq004MpnFormatEvidence = {
    status: DemoRuleStatus;
    mpn: string;
    manufacturerName: string;
    issues: Dq004MpnIssue[];
    expectedPrefix?: string;
    appGlueRuntimeError?: string;
    outputQueue?: DemoOutputQueue;
};

const createDq004InputDefinition = (): DataDefinition => {
    const inputData = new DataDefinition();
    inputData.fields.push(new StringDataDefinition("mpn"));
    inputData.fields.push(new StringDataDefinition("manufacturer_name"));
    return inputData;
};

export const buildDq004ExecutionData = (part: EnrichedPart): ExecutionData =>
    new ExecutionData({
        mpn: part.mpn,
        manufacturer_name: part.manufacturerName,
    });

const mpnEquals = (value: string): StringEqualExpression => {
    const expression = new StringEqualExpression();
    expression.value1.variableName = "mpn";
    expression.value2.value = value;
    return expression;
};

const mpnContains = (value: string): StringContainsExpression => {
    const expression = new StringContainsExpression();
    expression.value1.variableName = "mpn";
    expression.value2.value = value;
    return expression;
};

const mpnLengthLessThanOrEqualTo = (length: number): LessThanEqualToExpression => {
    const stringLength = new StringLengthExpression();
    stringLength.value1.variableName = "mpn";

    const expression = new LessThanEqualToExpression();
    expression.value1.subExpression = stringLength;
    expression.value2.value = length;
    return expression;
};

const manufacturerNameEquals = (manufacturerName: string): StringEqualExpression => {
    const expression = new StringEqualExpression();
    expression.value1.variableName = "manufacturer_name";
    expression.value2.value = manufacturerName;
    return expression;
};

const mpnStartsWith = (prefix: string): StringStartWithExpression => {
    const expression = new StringStartWithExpression();
    expression.value1.variableName = "mpn";
    expression.value2.value = prefix;
    return expression;
};

const mpnDoesNotStartWith = (prefix: string): NotExpression => {
    const expression = new NotExpression();
    expression.value1.subExpression = mpnStartsWith(prefix);
    return expression;
};

const setBranchExpressions = (branch: IfThenPair, expressions: ExpressionRule[]) => {
    branch.ifExpression.elements = [];
    expressions.forEach((expression) => {
        branch.ifExpression.add();
        branch.ifExpression.elements[branch.ifExpression.elements.length - 1]!.subExpression = expression;
    });
};

export const createDq004StatementRuleConfiguration = (): StatementRuleConfiguration => {
    AppGlueCoreInit.init();

    const config = new StatementRuleConfiguration();
    config.title = "DQ-004 MPN format validation";
    config.inputData = createDq004InputDefinition();
    config.executionResult = new BooleanDataDefinition();

    const expression = new IfThenExpression();
    expression.ifThenStyle = IfThenStyle.BRANCH_DATA_RETURN;

    const placeholderBranch = expression.ifBranches[0]!;
    setBranchExpressions(placeholderBranch, [mpnEquals(DQ004_PLACEHOLDER_MPN)]);
    placeholderBranch.thenExpression.value = false;

    const whitespaceBranch = expression.addIfBranch();
    setBranchExpressions(whitespaceBranch, [mpnContains(" ")]);
    whitespaceBranch.thenExpression.value = false;

    const shortBranch = expression.addIfBranch();
    setBranchExpressions(shortBranch, [mpnLengthLessThanOrEqualTo(DQ004_MIN_MPN_LENGTH)]);
    shortBranch.thenExpression.value = false;

    DQ004_MANUFACTURER_PREFIX_RULES.forEach((prefixRule) => {
        const branch = expression.addIfBranch();
        setBranchExpressions(branch, [
            manufacturerNameEquals(prefixRule.manufacturerName),
            mpnDoesNotStartWith(prefixRule.prefix),
        ]);
        branch.thenExpression.value = false;
    });

    expression.defaultOutput.value = true;
    config.expression.expression = expression;

    return config;
};

const getExpectedPrefix = (part: EnrichedPart): string | undefined =>
    DQ004_MANUFACTURER_PREFIX_RULES.find((rule) => rule.manufacturerName === part.manufacturerName)?.prefix;

export const getDq004MpnIssues = (part: EnrichedPart): Dq004MpnIssue[] => {
    const issues: Dq004MpnIssue[] = [];
    const expectedPrefix = getExpectedPrefix(part);

    if (part.mpn === DQ004_PLACEHOLDER_MPN) issues.push("placeholder");
    if (part.mpn.includes(" ")) issues.push("whitespace");
    if (part.mpn.length <= DQ004_MIN_MPN_LENGTH) issues.push("too_short");
    if (expectedPrefix && !part.mpn.startsWith(expectedPrefix)) issues.push("manufacturer_prefix_mismatch");

    return issues;
};

const evaluateDq004Part = (config: StatementRuleConfiguration, part: EnrichedPart) => {
    const { appGluePassed, appGlueRuntimeError } = runStatementRuleBoolean(config, buildDq004ExecutionData(part));
    const status: DemoRuleStatus = appGlueRuntimeError ? "rejected" : appGluePassed ? "pass" : "rejected";
    const expectedPrefix = getExpectedPrefix(part);

    return {
        appGluePassed,
        evidence: {
            status,
            mpn: part.mpn,
            manufacturerName: part.manufacturerName,
            issues: getDq004MpnIssues(part),
            expectedPrefix,
            appGlueRuntimeError,
            outputQueue: status === "rejected" ? (appGlueRuntimeError ? "pending_review" : DQ004_OUTPUT_QUEUE) : undefined,
        } satisfies Dq004MpnFormatEvidence,
    };
};

const DQ004_SUMMARY_PRESENTER = createNativeStatementRuleSummaryPresenter({
    actionLabel: (_config, context) =>
        context?.definition?.actions.find((action) => action.id === "quarantine-mpn")?.label ??
        "quarantine suspicious MPNs",
});

const formatIssues = (issues: Dq004MpnIssue[]): string => {
    if (issues.length === 0) return "configured native expression";
    return issues
        .map((issue) => {
            if (issue === "placeholder") return "placeholder value";
            if (issue === "whitespace") return "suspicious whitespace";
            if (issue === "too_short") return "too-short value";
            return "manufacturer prefix mismatch";
        })
        .join(", ");
};

export const DQ004_STATEMENT_RULE_POLICY: StatementRuleIntegrationPolicy<EnrichedPart> = {
    ruleId: DQ004_RULE_ID,
    policyBinding: requireRulePolicyBinding(DQ004_RULE_ID),
    createConfig: createDq004StatementRuleConfiguration,
    evaluator: {
        evaluate: (config, part) => {
            const { appGluePassed, evidence } = evaluateDq004Part(config, part);

            return {
                passed: appGluePassed,
                status: evidence.status,
                reasonCode: evidence.appGlueRuntimeError
                    ? "APPGLUE_EXPRESSION_INCOMPLETE"
                    : evidence.status === "pass"
                      ? "MPN_ACCEPTED"
                      : "SUSPICIOUS_MPN",
                explanation: evidence.appGlueRuntimeError
                    ? "The AppGlue expression is incomplete, so validation preview is holding rows until the expression is completed."
                    : evidence.status === "pass"
                        ? "The manufacturer part number passes the configured native format checks."
                        : `The manufacturer part number failed the configured native format checks: ${formatIssues(
                              evidence.issues,
                          )}.`,
                outputQueue: evidence.outputQueue,
                comparisons: evidence.appGlueRuntimeError
                    ? [
                          {
                              label: "AppGlue expression",
                              expected: "complete executable expression",
                              actual: evidence.appGlueRuntimeError,
                              ok: false,
                          },
                      ]
                    : [
                          {
                              label: "MPN",
                              expected: "configured native format checks",
                              actual: evidence.mpn,
                              ok: evidence.status !== "rejected",
                          },
                      ],
                runtimeEvidence: {
                    runtime: "appglue_runtime",
                    executableType: DQ004_APPGLUE_EXECUTABLE_TYPE,
                },
                evidence,
            };
        },
    },
    summaryPresenter: DQ004_SUMMARY_PRESENTER,
    runtimeSamples: {
        get passingRow() {
            return DQ004_RUNTIME_SAMPLES.passingRow;
        },
        get routedRow() {
            return DQ004_RUNTIME_SAMPLES.routedRow;
        },
    },
};

const samplePart = (overrides: Partial<EnrichedPart> = {}): EnrichedPart => ({
    partId: "PART-DQ004-SAMPLE",
    mpn: "VJ0001RES",
    manufacturerId: "MFR-004",
    manufacturerName: "Vishay",
    category: "Resistor",
    taxonomyPath: "Passive > Resistor > Thick Film",
    packageType: "0402",
    pinCount: null,
    voltageMin: 50,
    voltageMax: 50,
    capacitancePf: null,
    resistanceOhms: 10000,
    tolerancePct: 1,
    rohsStatus: "Compliant",
    reachStatus: "Compliant",
    lifecycleStatus: "Active",
    priorLifecycleStatus: "Active",
    confidenceScore: 93,
    source: "manufacturer_direct",
    recommendedReplacementMpn: null,
    hasMandatoryAttributeGap: false,
    hasUomConflict: false,
    hasMpnFormatIssue: false,
    hasComplianceChange: false,
    hasSourceConflict: false,
    ...overrides,
});

export const DQ004_RUNTIME_SAMPLES = {
    passingRow: samplePart(),
    routedRow: samplePart({
        partId: "PART-DQ004-ROUTED",
        mpn: " TBD ",
        hasMpnFormatIssue: true,
    }),
};
